import React, { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const FioriPathfinder = ({ project }) => {
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedWave, setSelectedWave] = useState('all');

  useEffect(() => {
    fetchMappings();
  }, [project.id]);

  const fetchMappings = async () => {
    try {
      const response = await axios.get(`${API}/projects/${project.id}/fiori-mappings`);
      setMappings(response.data);
    } catch (error) {
      console.error('Error fetching mappings:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredMappings = selectedWave === 'all'
    ? mappings
    : mappings.filter((m) => m.adoption_wave === selectedWave);

  const waveColors = {
    wave_1: 'bg-green-100 text-green-800',
    wave_2: 'bg-yellow-100 text-yellow-800',
    wave_3: 'bg-blue-100 text-blue-800'
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
    </div>;
  }

  const byWave = {
    wave_1: mappings.filter((m) => m.adoption_wave === 'wave_1'),
    wave_2: mappings.filter((m) => m.adoption_wave === 'wave_2'),
    wave_3: mappings.filter((m) => m.adoption_wave === 'wave_3')
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Fiori Pathfinder</h2>
        <p className="text-gray-600 mb-6">Transaction to Fiori App mapping and adoption planning</p>

        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4">
            <div className="text-green-600 text-2xl font-bold">{byWave.wave_1.length}</div>
            <div className="text-sm text-gray-700 font-medium mt-1">Wave 1 (Priority)</div>
            <div className="text-xs text-gray-500 mt-1">High-usage transactions</div>
          </div>
          <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4">
            <div className="text-yellow-600 text-2xl font-bold">{byWave.wave_2.length}</div>
            <div className="text-sm text-gray-700 font-medium mt-1">Wave 2 (Standard)</div>
            <div className="text-xs text-gray-500 mt-1">Medium-usage transactions</div>
          </div>
          <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
            <div className="text-blue-600 text-2xl font-bold">{byWave.wave_3.length}</div>
            <div className="text-sm text-gray-700 font-medium mt-1">Wave 3 (Later)</div>
            <div className="text-xs text-gray-500 mt-1">Low-usage transactions</div>
          </div>
        </div>

        <div className="mb-6">
          <select
            value={selectedWave}
            onChange={(e) => setSelectedWave(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Waves</option>
            <option value="wave_1">Wave 1 (Priority)</option>
            <option value="wave_2">Wave 2 (Standard)</option>
            <option value="wave_3">Wave 3 (Later)</option>
          </select>
        </div>

        <div className="space-y-3">
          {filteredMappings.map((mapping) => (
            <div key={mapping.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <span className="px-3 py-1 bg-gray-900 text-white font-mono text-sm rounded">
                      {mapping.transaction_code}
                    </span>
                    <span className="text-gray-600">→</span>
                    <span className="px-3 py-1 bg-blue-600 text-white font-mono text-sm rounded">
                      {mapping.fiori_app_id}
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${waveColors[mapping.adoption_wave]}`}>
                      {mapping.adoption_wave.replace('_', ' ')}
                    </span>
                  </div>
                  <div className="mb-2">
                    <div className="text-sm text-gray-700">
                      <span className="font-medium">From:</span> {mapping.transaction_name}
                    </div>
                    <div className="text-sm text-gray-700">
                      <span className="font-medium">To:</span> {mapping.fiori_app_name}
                    </div>
                  </div>
                  <div className="flex items-center space-x-4 text-sm">
                    <div>
                      <span className="text-gray-600">Usage Frequency: </span>
                      <span className="font-medium text-gray-900">{mapping.usage_frequency.toLocaleString()}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">User Role: </span>
                      <span className="font-medium text-gray-900">{mapping.user_role}</span>
                    </div>
                    <div>
                      {mapping.training_required && (
                        <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded text-xs font-medium">
                          Training Required
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
